'use client';

import React, { useState } from 'react';
import Icon from '@/components/ui/AppIcon';

interface CalendarIntegrationProps {
  eventTitle: string;
  eventDate: string;
  eventLocation: string;
  eventDescription: string;
}

const CalendarIntegration = ({
  eventTitle,
  eventDate,
  eventLocation,
  eventDescription,
}: CalendarIntegrationProps) => {
  const [showOptions, setShowOptions] = useState(false);
  const [savedStatus, setSavedStatus] = useState<string | null>(null);

  const formatDateForCalendar = (dateString: string) => {
    const date = new Date(dateString);
    return date.toISOString().replace(/[-:]/g, '').split('.')[0] + 'Z';
  };

  const handleGoogleCalendar = () => {
    const startDate = formatDateForCalendar(eventDate);
    const endDate = formatDateForCalendar(new Date(new Date(eventDate).getTime() + 4 * 60 * 60 * 1000).toISOString());
    
    const googleUrl = `https://calendar.google.com/calendar/render?action=TEMPLATE&text=${encodeURIComponent(
      eventTitle
    )}&dates=${startDate}/${endDate}&details=${encodeURIComponent(
      eventDescription
    )}&location=${encodeURIComponent(eventLocation)}`;

    window.open(googleUrl, '_blank');
    setSavedStatus('Google Calendar');
    setTimeout(() => setSavedStatus(null), 3000);
  };

  const handleAppleCalendar = () => {
    const startDate = formatDateForCalendar(eventDate);
    const endDate = formatDateForCalendar(new Date(new Date(eventDate).getTime() + 4 * 60 * 60 * 1000).toISOString());

    const icsContent = `BEGIN:VCALENDAR
VERSION:2.0
BEGIN:VEVENT
DTSTART:${startDate}
DTEND:${endDate}
SUMMARY:${eventTitle}
DESCRIPTION:${eventDescription}
LOCATION:${eventLocation}
END:VEVENT
END:VCALENDAR`;

    const blob = new Blob([icsContent], { type: 'text/calendar' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = 'wedding-event.ics';
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);

    setSavedStatus('Apple Calendar');
    setTimeout(() => setSavedStatus(null), 3000);
  };

  const handleOutlookCalendar = () => {
    const startDate = formatDateForCalendar(eventDate);
    const endDate = formatDateForCalendar(new Date(new Date(eventDate).getTime() + 4 * 60 * 60 * 1000).toISOString());

    const outlookUrl = `https://outlook.live.com/calendar/0/deeplink/compose?subject=${encodeURIComponent(
      eventTitle
    )}&startdt=${startDate}&enddt=${endDate}&body=${encodeURIComponent(
      eventDescription
    )}&location=${encodeURIComponent(eventLocation)}`;

    window.open(outlookUrl, '_blank');
    setSavedStatus('Outlook Calendar');
    setTimeout(() => setSavedStatus(null), 3000);
  };

  return (
    <section className="py-16 px-4 bg-background">
      <div className="max-w-4xl mx-auto text-center">
        <div className="bg-card rounded-lg p-8 sm:p-12 shadow-sacred-lg">
          <div className="flex justify-center mb-6">
            <div className="bg-primary/10 rounded-full p-4">
              <Icon name="CalendarIcon" size={48} className="text-primary" />
            </div>
          </div>

          <h2 className="font-playfair text-3xl sm:text-4xl font-semibold text-foreground mb-4">
            Save the Date
          </h2>
          <p className="font-inter text-base text-muted-foreground mb-8 max-w-2xl mx-auto">
            Mark your calendar and join us in celebrating this blessed union. Add our wedding to your calendar with one click.
          </p>

          {savedStatus && (
            <div className="mb-6 inline-flex items-center space-x-2 bg-success/10 text-success px-4 py-2 rounded-lg">
              <Icon name="CheckCircleIcon" size={20} />
              <span className="font-inter text-sm">Added to {savedStatus}!</span>
            </div>
          )}

          <div className="relative">
            <button
              onClick={() => setShowOptions(!showOptions)}
              className="w-full sm:w-auto inline-flex items-center justify-center space-x-2 bg-primary hover:bg-primary/90 text-primary-foreground px-8 py-4 rounded-lg font-inter font-medium transition-all duration-300 shadow-md hover:shadow-lg"
            >
              <Icon name="CalendarDaysIcon" size={24} />
              <span>Add to Calendar</span>
              <Icon
                name="ChevronDownIcon"
                size={20}
                className={`transition-transform duration-300 ${showOptions ? 'rotate-180' : ''}`}
              />
            </button>

            {showOptions && (
              <div className="absolute left-1/2 transform -translate-x-1/2 mt-2 w-64 bg-card rounded-lg shadow-sacred-lg overflow-hidden z-10">
                <button
                  onClick={handleGoogleCalendar}
                  className="w-full flex items-center space-x-3 px-6 py-4 hover:bg-surface transition-colors duration-200 text-left"
                >
                  <Icon name="CalendarIcon" size={20} className="text-primary" />
                  <span className="font-inter text-sm text-foreground">Google Calendar</span>
                </button>
                <button
                  onClick={handleAppleCalendar}
                  className="w-full flex items-center space-x-3 px-6 py-4 hover:bg-surface transition-colors duration-200 text-left border-t border-border"
                >
                  <Icon name="CalendarIcon" size={20} className="text-primary" />
                  <span className="font-inter text-sm text-foreground">Apple Calendar</span>
                </button>
                <button
                  onClick={handleOutlookCalendar}
                  className="w-full flex items-center space-x-3 px-6 py-4 hover:bg-surface transition-colors duration-200 text-left border-t border-border"
                >
                  <Icon name="CalendarIcon" size={20} className="text-primary" />
                  <span className="font-inter text-sm text-foreground">Outlook Calendar</span>
                </button>
              </div>
            )}
          </div>

          <p className="font-inter text-xs text-muted mt-6">
            Choose your preferred calendar application to receive reminders
          </p>
        </div>
      </div>
    </section>
  );
};

export default CalendarIntegration;